import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  getAccountingEntryClasses,
  getAccountingEntryTypes,
  getAccountingNodes,
  getAccountingPeriods,
} from 'lib/api/accounting';
import CustomAlert from 'lib/designSystem/Alert';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../../../../../../services/redux/store';
import {
  AccountingEntry,
  AccountingEntryClass,
  AccountingLine,
  AccountingNode,
  AccountingPeriod,
  EntryType,
  MajorBookModalEntry,
} from '../../../../types';
import EntryWithLines from '../../../AccountingEntries/components/EntryWithLines/EditEntry';
import EntryModal, {
  type ModalEntry,
} from '../../../BookEntries/components/Modal';
import { BookExcelLine, exportBookToExcel } from '../../../utils/exportToExcel';

const useStyles = makeStyles(() =>
  createStyles({
    tableCell: {
      padding: '8px !important',
      overflow: 'hidden',
      minWidth: '50px',
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important',
    },
    clickableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
  }),
);

function isMajorBookModalEntry(
  entry: ModalEntry,
): entry is MajorBookModalEntry {
  return entry.groupedLines.length > 0 && 'nodeId' in entry.groupedLines[0];
}

interface MovementsTableProps {
  groupedLines: AccountingLine[];
  groupedByCategoryAndBusiness: Record<
    string,
    Record<string, AccountingLine[]>
  >;
  refreshData?: (showLoading?: boolean) => Promise<void>;
  startDate?: Date;
  endDate?: Date;
  balanceType?: string;
  detailCode?: string;
  detailName?: string;
}

const MovementsTable = ({
  groupedLines,
  groupedByCategoryAndBusiness,
  refreshData,
  startDate,
  endDate,
  balanceType = 'Libro Mayor',
  detailCode = '',
  detailName = 'Cuenta',
}: MovementsTableProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<MajorBookModalEntry | null>(null);
  const [entryWithLinesOpen, setEntryWithLinesOpen] = useState(false);
  const [entryForEditing, setEntryForEditing] =
    useState<AccountingEntry | null>(null);
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [nodes, setNodes] = useState<AccountingNode[]>([]);
  const [entryClasses, setEntryClasses] = useState<AccountingEntryClass[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<
    AccountingPeriod[]
  >([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>(
    'error',
  );
  const user = useSelector((state: RootState) => state.auth.user);

  const handleRowClick = (row: AccountingLine) => {
    const foundGroup = findGroupByMovementKey(row);
    setSelectedCategory(foundGroup);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  const setUpEntryEditing = async () => {
    try {
      const response = await getAccountingEntryTypes(dispatch);
      const responseNodes = await getAccountingNodes(dispatch);
      const responseEntryClasses = await getAccountingEntryClasses(dispatch);
      const responsePeriods = await getAccountingPeriods(dispatch);

      setEntryTypes(response);
      setNodes(responseNodes);
      setEntryClasses(responseEntryClasses);
      setAccountingPeriods(responsePeriods);
    } catch (error) {
      console.error('Error fetching entry editing data:', error);
    }
  };

  const setUp = async (_entryId?: number) => {
    if (refreshData) {
      await refreshData(false);
    }
    setEntryWithLinesOpen(false);
    setEntryForEditing(null);
  };

  const handleSetSuccessText = (text: string | null) => {
    if (text) {
      setAlertText(text);
      setAlertSeverity('success');
      setAlertOpen(true);
    }
  };

  const handleSetFailedText = (text: string | null) => {
    if (text) {
      setAlertText(text);
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  const handleAlertClose = (open: boolean) => {
    setAlertOpen(open);
  };

  const findGroupByMovementKey = (
    row: AccountingLine,
  ): MajorBookModalEntry | null => {
    const formattedDate = formatDate(row.date, 'yyyy-mm-dd');
    const dateGroups = groupedByCategoryAndBusiness?.[formattedDate];
    if (!dateGroups) {
      return null;
    }
    const allEntries = Object.values(dateGroups).flat();
    const matchingEntry = allEntries.find(
      (entry: AccountingLine) => entry.movementKey === row.movementKey,
    );
    if (matchingEntry) {
      const categoryAndAccount = Object.keys(dateGroups).find((key) =>
        dateGroups[key].some(
          (entry: AccountingLine) => entry.movementKey === row.movementKey,
        ),
      );
      return {
        date: formattedDate,
        category: matchingEntry.category,
        groupedLines: dateGroups[categoryAndAccount!],
        businessName: matchingEntry.businessName,
        businessIdentifier: matchingEntry.businessIdentifier,
      };
    }
    return null;
  };

  const handleExportToExcel = async () => {
    const linesForExport = groupedLines.map((l) => ({
      ...l,
      entryId: String(l.entryId ?? ''),
      date:
        l.date instanceof Date
          ? l.date.toISOString().slice(0, 10)
          : String(l.date),
    }));

    const isLibroAuxiliar = balanceType === 'Libro Auxiliar';
    await exportBookToExcel(linesForExport as unknown as BookExcelLine[], {
      startDate,
      endDate,
      businessIdentifier: user?.businessIdentifier,
      businessName: user?.companyName,
      ...(isLibroAuxiliar
        ? {
            businessAccountIdentifier: detailCode,
            businessAccountName: detailName,
          }
        : { accountCode: detailCode, accountName: detailName }),
      balanceType,
      includeAccountInfo: true,
    });
  };

  useEffect(() => {
    void setUpEntryEditing();
  }, []);

  const handleEditEntry = (entry: ModalEntry) => {
    if (!isMajorBookModalEntry(entry)) {
      return;
    }
    const lines = entry.groupedLines;
    const firstLine = lines?.[0];
    if (!firstLine) {
      console.error('No lines found in entry');
      return;
    }

    let classId = firstLine.classId ?? null;
    if (!classId && firstLine.className && entryClasses.length > 0) {
      // Try to find classId by matching className
      const matchingClass = entryClasses.find(
        (ec: AccountingEntryClass) => ec.name === firstLine.className,
      );
      if (matchingClass) {
        classId = matchingClass.id;
      }
    }

    const entryForEdit: AccountingEntry = {
      id: firstLine.entryId ?? undefined,
      name: entry.category,
      typeId: firstLine.typeId ?? 'free',
      classId: classId,
      date:
        entry.date ||
        (firstLine.date instanceof Date
          ? firstLine.date.toISOString().slice(0, 10)
          : String(firstLine.date ?? '')) ||
        new Date().toISOString().slice(0, 10),
      sourceKey:
        entry.sourceKey ?? String(firstLine.entryId ?? '') ?? undefined,
      status: 'CLOSED',
      companyId: user?.companyId ?? 0,
      lines:
        lines?.map((line) => ({
          id: line.entryId ?? undefined,
          nodeId: line.nodeId ?? null,
          debit: line.debit ?? 0,
          credit: line.credit ?? 0,
          costCenterId: line.costCenterId ?? null,
          documentKey: line.documentId ?? '',
          businessIdentifier:
            line.businessIdentifier ?? line.auxiliaryBusinessIdentifier ?? '',
          description: line.description ?? '',
          node: undefined,
        })) ?? [],
    };

    setEntryForEditing(entryForEdit);
    setEntryWithLinesOpen(true);
    setModalOpen(false);
  };

  return (
    <>
      <Paper elevation={0} style={{ padding: 16, margin: '12px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 8,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {groupedLines[0].accountName}
          </Typography>
          <IconButton
            onClick={() => {
              void handleExportToExcel();
            }}
            size="small"
            color="primary"
            title="Exportar a Excel"
          >
            <FileDownloadIcon />
          </IconButton>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID Asiento</TableCell>
              <TableCell>Clase</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Aux</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Debe</TableCell>
              <TableCell>Haber</TableCell>
              <TableCell>Débito</TableCell>
              <TableCell>Crédito</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedLines.map((line: AccountingLine, catIndex: number) => (
              <React.Fragment key={catIndex}>
                <TableRow
                  className={classes.clickableRow}
                  onClick={() => handleRowClick(line)}
                >
                  <TableCell className={classes.tableCell}>
                    {line.entryId}
                  </TableCell>
                  <TableCell>{line.className ?? line.category}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(line.date, 'dd-mm-yyyy')}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {line.description}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {line.businessIdentifier}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {line.documentId}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(line.debit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(line.credit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(line.debitFinal ?? 0)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(line.creditFinal ?? 0)}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            <TableRow>
              <TableCell
                colSpan={6}
                style={{
                  fontWeight: 'bold',
                  textAlign: 'left',
                }}
              >
                Totales:
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, l: AccountingLine) => sum + (l.debit ?? 0),
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, l: AccountingLine) => sum + (l.credit ?? 0),
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, l: AccountingLine) =>
                      sum + (l.debitFinal ?? 0),
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, l: AccountingLine) =>
                      sum + (l.creditFinal ?? 0),
                    0,
                  ),
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {selectedCategory && (
          <EntryModal
            open={modalOpen}
            onEdit={handleEditEntry}
            onClose={handleCloseModal}
            entry={selectedCategory}
          />
        )}
        {entryWithLinesOpen && entryForEditing && (
          <EntryWithLines
            entry={entryForEditing}
            setEntry={(updatedEntry: AccountingEntry) => {
              setEntryForEditing(updatedEntry);
            }}
            open={entryWithLinesOpen}
            setOpen={(open: boolean) => {
              setEntryWithLinesOpen(open);
            }}
            setUp={setUp}
            setSuccessText={handleSetSuccessText}
            setFailedText={handleSetFailedText}
            entryTypes={entryTypes}
            nodes={nodes}
            entryClasses={entryClasses}
            accountingPeriods={accountingPeriods}
          />
        )}
        <CustomAlert
          open={alertOpen}
          setOpen={handleAlertClose}
          severity={alertSeverity}
          title={alertSeverity === 'success' ? 'Éxito' : 'Error'}
          text={alertText}
        />
      </Paper>
    </>
  );
};

export default MovementsTable;
